import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, X, Sparkles, Apple, Camera } from "lucide-react";
import { toast } from "sonner";
import SnapToLog from "@/components/SnapToLog";

const MEALS = [
  { v: "breakfast", l: "Breakfast" },
  { v: "lunch", l: "Lunch" },
  { v: "dinner", l: "Dinner" },
  { v: "snack", l: "Snack" },
];

function MacroBar({ label, current, target, color }) {
  const pct = target ? Math.min(100, (current / target) * 100) : 0;
  return (
    <div data-testid={`macro-${label}`}>
      <div className="flex justify-between font-mono text-[11px]">
        <span className="text-white/60 uppercase tracking-wider">{label}</span>
        <span className="text-white">
          {Math.round(current)}{target ? ` / ${Math.round(target)}` : ""}
          <span className="text-white/30 ml-1">{label === "calories" ? "kcal" : "g"}</span>
        </span>
      </div>
      <div className="h-1.5 bg-white/5 mt-1">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 10px ${color}` }} />
      </div>
    </div>
  );
}

export default function NutritionTracker() {
  const { user, loading: authLoading } = useAuth();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [meal, setMeal] = useState("breakfast");
  const [adding, setAdding] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const reload = async () => {
    const { data } = await api.get("/nutrition/log");
    setLog(data);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const add = async (e) => {
    e?.preventDefault();
    if (!text.trim() || adding) return;
    setAdding(true);
    try {
      await api.post("/nutrition/log", { text: text.trim(), meal });
      setText("");
      toast.success("Logged");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (entryId) => {
    try {
      await api.delete(`/nutrition/log/${entryId}`);
      await reload();
    } catch {
      toast.error("Could not delete");
    }
  };

  if (loading || !log) {
    return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF3333]" /></div>;
  }

  const t = log.totals;
  const tg = log.targets || {};
  const byMeal = MEALS.map((m) => ({
    ...m,
    items: log.entries.filter((e) => e.meal === m.v),
  }));

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10" data-testid="nutrition-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF]">FUEL · {today}</div>
          <h1 className="font-heading uppercase text-5xl md:text-6xl leading-[0.9] mt-2">Macro Tracker</h1>
        </div>
        <div className="font-jp text-5xl text-white/10">食</div>
      </div>

      {/* Targets vs actuals */}
      <div className="brutal-border bg-[#0a0a0a] p-6 grid md:grid-cols-4 gap-6">
        <MacroBar label="calories" current={t.calories} target={tg.calories} color="#FF3333" />
        <MacroBar label="protein" current={t.protein_g} target={tg.protein_g} color="#00E5FF" />
        <MacroBar label="carbs" current={t.carbs_g} target={tg.carbs_g} color="#FFB800" />
        <MacroBar label="fat" current={t.fat_g} target={tg.fat_g} color="#9CA3AF" />
      </div>
      {!tg.calories && (
        <div className="font-mono text-[10px] text-white/30 tracking-widest mt-2">
          NO TARGETS · UNLOCK YOUR AI PLAN TO SEE GOALS
        </div>
      )}

      {/* Quick add */}
      <form onSubmit={add} className="brutal-border bg-[#0a0a0a] p-5 mt-6" data-testid="nutrition-add-form">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#FF3333]" />
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3333]">AI QUICK LOG</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. 2 eggs and oatmeal with banana"
            data-testid="nutrition-input"
            className="flex-1 min-w-[200px] bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-4 py-3 font-sub text-base"
          />
          <select
            value={meal}
            onChange={(e) => setMeal(e.target.value)}
            data-testid="nutrition-meal"
            className="bg-black border border-white/15 px-3 py-3 font-sub text-sm focus:border-[#FF3333] outline-none"
          >
            {MEALS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <button type="submit" disabled={adding} data-testid="nutrition-add" className="btn-forged-red disabled:opacity-50">
            {adding ? <><Loader2 className="w-4 h-4 animate-spin" /> AI parsing</> : <><Plus className="w-4 h-4" /> Log</>}
          </button>
          <button
            type="button"
            onClick={() => setSnapOpen(true)}
            data-testid="open-snap-to-log"
            className="btn-forged-ghost"
            title="Snap a photo of food, a barcode, or a nutrition label"
          >
            <Camera className="w-4 h-4" /> Snap
          </button>
        </div>
        <div className="font-mono text-[10px] text-white/40 tracking-widest mt-3">
          TYPE IT · OR TAP <span className="text-[#FF3333]">SNAP</span> TO USE YOUR CAMERA &middot; BARCODES &middot; LABELS &middot; PLATES
        </div>
      </form>

      {/* Meals */}
      <div className="mt-8 space-y-6">
        {byMeal.map((m) => (
          <div key={m.v} data-testid={`meal-section-${m.v}`}>
            <div className="flex items-center gap-2 mb-3">
              <Apple className="w-4 h-4 text-[#00E5FF]" />
              <div className="font-heading uppercase text-lg">{m.l}</div>
              <div className="font-mono text-[10px] text-white/40 tracking-widest">
                {Math.round(m.items.reduce((s, i) => s + i.calories, 0))} kcal
              </div>
            </div>
            {m.items.length === 0 ? (
              <div className="brutal-border bg-[#0a0a0a]/40 p-4 font-mono text-[11px] text-white/30 tracking-widest">
                NO ENTRIES
              </div>
            ) : (
              <div className="space-y-2">
                {m.items.map((e) => (
                  <div key={e.id} className="brutal-border bg-[#0a0a0a] p-4 flex items-center gap-4" data-testid={`food-entry-${e.id}`}>
                    <div className="flex-1">
                      <div className="font-sub text-white">{e.name}</div>
                      <div className="font-mono text-[10px] text-white/40 tracking-wider mt-1">
                        P {e.protein_g}g · C {e.carbs_g}g · F {e.fat_g}g
                      </div>
                    </div>
                    <div className="font-heading text-xl text-[#FF3333]">{Math.round(e.calories)}</div>
                    <button onClick={() => remove(e.id)} className="text-white/30 hover:text-[#FF3333]" data-testid={`food-remove-${e.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {snapOpen && (
        <SnapToLog
          defaultMeal={meal}
          onClose={() => setSnapOpen(false)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
